import { useEffect } from "react";
import ErrorAlert from "../../ErrorAlert";
import { Loading } from "../../Loading";
import { useUnifiedAuth } from "../../unified-auth";
import {
  detectAuthenticationResults,
  isSilentLoginFallbackCandidate,
  parseAuthenticationResults,
  redirectToErrorPage,
} from "./utils";

/**
 * This callback page is just a basic landing page to end up on when a user is redirected from the IdP, if
 * you land here you will just either see an error page or a loading page.
 * @constructor
 */
export default function Callback() {
  const { isLoading, error } = useUnifiedAuth();

  useEffect(() => {
    // Restored from the back/forward cache (e.g. Back from the IdP after the interactive
    // fallback): reload, so the page doesn't wait on a callback it has already handled.
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        window.location.reload();
      }
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  const callbackResults = parseAuthenticationResults();
  if (detectAuthenticationResults(callbackResults)) {
    // Wait for the IdP client to fall back to an interactive login, or to set `error`.
    if (isSilentLoginFallbackCandidate(callbackResults) && !error) {
      return <Loading />;
    }
    // Any other error is redirected to the error page with its error message
    redirectToErrorPage(callbackResults);
    return null;
  }

  if (isLoading) {
    return <Loading />;
  }

  if (error) {
    if (error instanceof Error) {
      const opts = { error: error.message };
      if (detectAuthenticationResults(opts)) {
        redirectToErrorPage(opts);
        return null; // Redirects to the error page instead of rendering
      }
    }
    return <ErrorAlert input={error} />;
  }

  return (
    <>
      <main className="login"></main>
    </>
  );
}
